import React from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { api, inr, fileUrl, isOnSale } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Heart, ShoppingBag, Ruler, Award, Truck } from "lucide-react";
import { ProductCard, GlassBadge, WishlistButton } from "@/components/ProductCard";
import { ProductReviews, Stars } from "@/components/Reviews";
import { ColorDot, splitColors } from "@/lib/colors";
import { toast } from "sonner";

export default function ProductDetail() {
  const { slug } = useParams();
  const { addToCart, user, wishlist, toggleWishlist } = useAuth();
  const nav = useNavigate();
  const { data: product, isLoading } = useQuery({ queryKey: ["product", slug], queryFn: () => api.get(`/products/${slug}`).then(r => r.data) });
  const [activeImg, setActiveImg] = React.useState(0);
  const [zoom, setZoom] = React.useState({ show: false, x: 0, y: 0 });

  if (isLoading) return <div className="max-w-7xl mx-auto px-4 py-20"><div className="animate-pulse h-96 bg-neutral-100"/></div>;
  if (!product) return <div className="max-w-7xl mx-auto px-4 py-20">Not found.</div>;

  const images = product.images?.length ? product.images : ["https://images.pexels.com/photos/36951188/pexels-photo-36951188.jpeg?auto=compress&cs=tinysrgb&h=940&w=940"];



  const saved = wishlist?.has(product.id);
  const addWishlist = async () => {
    if (!user) { toast("Sign in to save pieces to your wishlist"); return nav(`/login?redirect=/product/${product.slug}`); }
    await toggleWishlist(product);
  };

  return (
    <div>
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-10">
        <div className="text-xs uppercase tracking-widest text-neutral-500 mb-6">
          <Link to="/shop" className="hover:text-[#0E4D34]">Shop</Link> / {product.name}
        </div>
        <div className="grid md:grid-cols-2 gap-10 lg:gap-16">
          <div>
            <div
              className="relative aspect-[4/5] bg-neutral-100 overflow-hidden mb-4 cursor-zoom-in rounded-2xl"
              onMouseMove={e => {
                const rect = e.currentTarget.getBoundingClientRect();
                setZoom({ show: true, x: ((e.clientX - rect.left) / rect.width) * 100, y: ((e.clientY - rect.top) / rect.height) * 100 });
              }}
              onMouseLeave={() => setZoom({ show: false, x: 0, y: 0 })}
              data-testid="product-image-main"
            >
              {/* blurred copy fills the empty sides so the full photo can be shown without cropping */}
              <img src={fileUrl(images[activeImg])} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-60"/>
              <img src={fileUrl(images[activeImg])} alt={product.name} data-testid="product-main-img"
                   className="relative w-full h-full object-contain transition-transform duration-200"
                   style={zoom.show ? { transform: `scale(1.8)`, transformOrigin: `${zoom.x}% ${zoom.y}%` } : {}}/>
              {(isOnSale(product) || product.featured || product.badge) && (
                <div className="absolute top-4 left-4 flex flex-col items-start gap-2 pointer-events-none">
                  {isOnSale(product) && <GlassBadge tone="red">Sale</GlassBadge>}
                  {product.featured && <GlassBadge tone="green">Featured</GlassBadge>}
                  {product.badge && <GlassBadge tone="gold">{product.badge}</GlassBadge>}
                </div>
              )}
              <WishlistButton product={product} size={24} className="absolute top-3 right-3"/>
            </div>
            <div className="flex gap-3 overflow-x-auto no-scrollbar">
              {images.map((im, i) => (
                <button key={i} onClick={() => setActiveImg(i)} className={`w-16 sm:w-20 shrink-0 aspect-[4/5] bg-neutral-100 overflow-hidden rounded-xl border-2 ${activeImg === i ? "border-[#0E4D34]" : "border-transparent"}`} data-testid={`thumb-${i}`}>
                  <img src={fileUrl(im)} className="w-full h-full object-contain" alt={`thumb${i}`}/>
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="overline mb-2">{product.fabric || "Handcrafted"}</div>
            <h1 className="h1-editorial mb-3">{product.name}</h1>
            <a href="#reviews" className="inline-flex items-center gap-2 mb-4 text-sm text-neutral-600 hover:text-[#0E4D34]" data-testid="rating-link">
              <Stars value={product.rating_avg || 0} size={16}/>
              {product.rating_count ? <span><b className="text-neutral-900">{Number(product.rating_avg).toFixed(1)}</b> · {product.rating_count} review{product.rating_count > 1 ? "s" : ""}</span> : <span>Be the first to review</span>}
            </a>
            <div className="flex items-baseline gap-3 mb-6">
              <span className="text-3xl font-semibold text-[#0E4D34]">{inr(product.price)}</span>
              {isOnSale(product) && Number(product.compare_at_price) > Number(product.price) && <span className="text-neutral-400 line-through text-lg">{inr(product.compare_at_price)}</span>}
              <span className="text-xs text-neutral-500 ml-2">inc. of taxes</span>
            </div>
            <p className="text-neutral-700 leading-relaxed mb-6">{product.description}</p>

            <div className="border-t border-b py-4 my-6 grid grid-cols-2 gap-3 text-sm">
              {[["Fabric", product.fabric], ["Colour", product.color], ["Occasion", product.occasion]].filter(([, v]) => v).map(([k, v]) => (
                <div key={k} className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-neutral-500 text-xs uppercase tracking-widest">{k}:</span>
                  {k === "Colour"
                    ? splitColors(v).map((c) => (
                        <span key={c} className="inline-flex items-center gap-1.5 font-semibold mr-2" data-testid="colour-swatch"><ColorDot color={c} size={16}/>{c}</span>
                      ))
                    : <span className="font-semibold">{v}</span>}
                </div>
              ))}
            </div>

            <div className="mb-6">
              <div className="text-xs uppercase tracking-widest mb-2">
                Availability: {product.stock > 0 ? <span className="text-[#0E4D34] font-semibold">{product.stock} in stock (one-of-a-kind pieces)</span> : <span className="text-[#991B1B]">Sold out</span>}
              </div>
            </div>

            <div className="flex gap-3 mb-8">
              <button onClick={() => addToCart(product)} disabled={product.stock === 0} data-testid="btn-add-to-cart" className="btn-silk flex-1 disabled:opacity-40">
                <ShoppingBag size={14} className="mr-2"/> Add to Bag
              </button>
              <button onClick={addWishlist} data-testid="btn-add-wishlist" aria-label={saved ? "Remove from wishlist" : "Add to wishlist"} className="btn-outline-silk px-5"><Heart size={14} fill={saved ? "currentColor" : "none"}/></button>
            </div>


            <div className="grid grid-cols-3 gap-4 mt-8 pt-8 border-t text-xs text-neutral-600">
              <div><Truck className="mb-2 text-[#0E4D34]" size={20}/>Free shipping ₹3000+</div>
              <div><Award className="mb-2 text-[#0E4D34]" size={20}/>Handloom certified</div>
              <div><Ruler className="mb-2 text-[#0E4D34]" size={20}/>Size chart included</div>
            </div>
          </div>
        </div>

        {/* Reviews */}
        <ProductReviews product={product}/>

        {/* Related */}
        {(product.related || []).length > 0 && (
          <div className="mt-24">
            <div className="overline mb-2">You may also love</div>
            <h2 className="h2-editorial mb-8">Related Pieces</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-8">
              {product.related.map((p, i) => <ProductCard key={p.id} product={p} index={i}/>)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
